/* eslint id-length: ["error", { "exceptions": ["$"] }] */
/* eslint max-lines: ["error", {"max": 350, "skipBlankLines": true}] */


/**
 * A plain Javascript component that handles the comments.
 *
 * @class
 * @augments Component
 */

// This is necessary for testing purposes
const $ = window.$;

import Rails from "@rails/ujs";

import { createCharacterCounter } from "src/decidim/input_character_counter";
import ExternalLink from "src/decidim/external_link";
import updateExternalDomainLinks from "src/decidim/external_domain_warning";

export default class CommentsComponent {
  constructor($element, config) {
    this.$element = $element;
    this.commentableGid = config.commentableGid;
    this.commentsUrl = config.commentsUrl;
    this.rootDepth = config.rootDepth;
    this.order = config.order;
    this.lastCommentId = config.lastCommentId;
    this.commentsCount = config.commentsCount;
    this.pollingInterval = config.pollingInterval || 10000;
    this.singleComment = config.singleComment;
    this.toggleTranslations = config.toggleTranslations;
    this.id = this.$element.attr("id") || this._getUID();
    this.mounted = false;
    this.maxPages = Math.ceil(config.commentsCount / 30);
    this.currentPage = 1;
    this.reordered = false;
    this.isLoading = false;
    this.isFetchingComments = false;
    this.polledPages = new Set();
  }

  /**
   * Handles the logic for mounting the component
   * @public
   * @returns {Void} - Returns nothing
   */
  mountComponent() {
    if (this.$element.length > 0 && !this.mounted) {
      this.mounted = true;
      this._initializeComments(this.$element);
      if (!this.singleComment) {
        $(".add-comment textarea", this.$element).prop("disabled", true);
        this.isLoading = true;
        this._setLoading()
        this._fetchComments(() => {
          $(".add-comment textarea", this.$element).prop("disabled", false);
        });
      }

      $(".order-by__dropdown .is-submenu-item a", this.$element).on("click.decidim-comments", () => this._onInitOrder());

      this._initializeLoadMoreButton();
    }
  }

  /**
   * Handles the logic for unmounting the component
   * @public
   * @returns {Void} - Returns nothing
   */
  unmountComponent() {
    if (this.mounted) {
      this.mounted = false;
      this._stopPolling();

      $(".add-comment .opinion-toggle .button", this.$element).off("click.decidim-comments");
      $(".add-comment textarea", this.$element).off("input.decidim-comments");
      $(".order-by__dropdown .is-submenu-item a", this.$element).off("click.decidim-comments");
      $(".add-comment form", this.$element).off("submit.decidim-comments");
      $(".add-comment textarea", this.$element).each((_i, el) => el.removeEventListener("emoji.added", this._onTextInput));

      const loadMoreButton = document.getElementById("load-more-comments");
      if (loadMoreButton) {
        loadMoreButton.removeEventListener("click", this._loadMoreHandler);
      }
    }
  }

  _initializeLoadMoreButton() {
    const loadMoreButton = document.getElementById("load-more-comments");
    if (loadMoreButton) {
      this._loadMoreHandler = (event) => {
        event.preventDefault();
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(() => {
          if (!this.isFetchingComments) {
            const page = parseInt(loadMoreButton.getAttribute("data-page"), 10);
            const newPage = page + 1;
            this._loadMoreComments(newPage);
          }
        }, 300);
      };

      loadMoreButton.addEventListener("click", this._loadMoreHandler);
    }
  }

  /**
   * Adds a new thread to the comments section.
   * @public
   * @param {String} threadHtml - The HTML content for the thread.
   * @param {Boolean} fromCurrentUser - A boolean indicating whether the user
   *   herself was the author of the new thread. Defaults to false.
   * @returns {Void} - Returns nothing
   */
  addThread(threadHtml, fromCurrentUser = false) {
    const $parent = $(".comments:first", this.$element);
    const $comment = $(threadHtml);
    const $threads = $(".comment-threads", this.$element);
    this._addComment($threads, $comment);
    this._finalizeCommentCreation($parent, fromCurrentUser);
  }

  _loadMoreComments(page) {
    if (!this.isFetchingComments && page <= this.maxPages && !this.polledPages.has(page)) {
      this.currentPage = page;
      this.isLoading = true;
      this._setLoading();
      this._fetchComments(() => {
        const loadMoreButton = document.getElementById("load-more-comments");
        if (loadMoreButton) {
          loadMoreButton.setAttribute("data-page", this.currentPage);
        }
      }, page);
    }
  }

  addNewThread(threadHtml, fromCurrentUser = false) {
    const $parent = $(".comments:first", this.$element);
    const $comment = $(threadHtml);
    const $threads = $(".comment-threads", this.$element);
    this._addNewComment($threads, $comment);
    this._finalizeCommentCreation($parent, fromCurrentUser);
  }

  anchor() {
    setTimeout(() => {
      const $newComment = $("#comment_" + this.lastCommentId);
      if ($newComment.length) {
        const commentTop = $newComment.offset().top;
        const commentHeight = $newComment.height();
        $('html, body').animate({
          scrollTop: commentTop - commentHeight
        }, 150);
      } else {
        console.error("Comentario nao achado:", "comment_" + this.lastCommentId);
      }
    }, 180);
  }

  /**
   * Adds a new reply to an existing comment.
   * @public
   * @param {Number} commentId - The ID of the comment for which to add the
   *   reply to.
   * @param {String} replyHtml - The HTML content for the reply.
   * @param {Boolean} fromCurrentUser - A boolean indicating whether the user
   *   herself was the author of the new reply. Defaults to false.
   * @returns {Void} - Returns nothing
   */
  addReply(commentId, replyHtml, fromCurrentUser = false) {
    const $parent = $(`#comment_${commentId}`);
    const $comment = $(replyHtml);
    const $replies = $(`#comment-${commentId}-replies`);
    this._addComment($replies, $comment);
    $replies.siblings(".comment__additionalreply").removeClass("hide");
    // Hide the reply button and form in the actions section after a reply is added
    // This prevents users from adding multiple replies to the same comment
    const $commentActions = $parent.find(".comment__actions");
    if ($commentActions.length) {
      $commentActions.addClass("hide");
    }
    
    // Hide the reply form (both the one in actions and the one in show.erb)
    const $replyForm = $parent.find(`#comment${commentId}-reply`);
    if ($replyForm.length) {
      $replyForm.addClass("hide");
    }
    
    // Also hide the form in the additionalreply section if it exists
    const $additionalReplyForm = $parent.find(".comment__additionalreply");
    if ($additionalReplyForm.length) {
      $additionalReplyForm.addClass("hide");
    }
    
    // Show the "show replies" button if it was hidden
    const $showRepliesBtn = $parent.find(".comment__hide");
    if ($showRepliesBtn.length && $showRepliesBtn.hasClass("hide")) {
      $showRepliesBtn.removeClass("hide");
    }
    
    this._finalizeCommentCreation($parent, fromCurrentUser);
  }



  /**
   * Generates a unique identifier for the form.
   * @private
   * @returns {String} - Returns a unique identifier
   */
  _getUID() {
    return `comments-${new Date().setUTCMilliseconds()}-${Math.floor(Math.random() * 10000000)}`;
  }

  /**
   * Initializes the comments for the given parent element.
   * @private
   * @param {jQuery} $parent The parent element to initialize.
   * @returns {Void} - Returns nothing
   */
  _initializeComments($parent) {
    $(".add-comment", $parent).each((_i, el) => {
      const $add = $(el);
      const $form = $("form", $add);
      const $opinionButtons = $(".opinion-toggle .button", $add);
      const $text = $("textarea", $form);

      $opinionButtons.on("click.decidim-comments", this._onToggleOpinion);
      $text.on("input.decidim-comments", this._onTextInput);

      $(document).trigger("attach-mentions-element", [$text.get(0)]);

      $form.on("submit.decidim-comments", () => {
        const $submit = $("button[type='submit']", $form);

        $submit.attr("disabled", "disabled");
        this._stopPolling();
      });

      if ($text.length && $text.get(0) !== null) {
        // Attach event to the DOM node, instead of the jQuery object
        $text.get(0).addEventListener("emoji.added", this._onTextInput);
      }
    });
  }

  /**
   * Adds the given comment element to the given target element and
   * initializes it.
   * @private
   * @param {jQuery} $target - The target element to add the comment to.
   * @param {jQuery} $container - The comment container element to add.
   * @returns {Void} - Returns nothing
   */
  _addComment($target, $container) {
    let $comment = $(".comment", $container);
    if ($comment.length < 1) {
      // In case of a reply
      $comment = $container;
    }
    this.lastCommentId = parseInt($comment.data("comment-id"), 10);

    $target.append($container);
    $container.foundation();
    this._initializeComments($container);
    createCharacterCounter($(".add-comment textarea", $container));
    $container.find('a[target="_blank"]').each((_i, elem) => {
      const $link = $(elem);
      $link.data("external-link", new ExternalLink($link));
    });
    updateExternalDomainLinks($container);
  }

  _addNewComment($target, $container) {
    let $comment = $(".comment", $container);
    if ($comment.length < 1) {
      $comment = $container;
    }
    this.lastCommentId = parseInt($comment.data("comment-id"), 10);

    $target.prepend($container);
    $container.foundation();
    this._initializeComments($container);
    createCharacterCounter($(".add-comment textarea", $container));
    $container.find('a[target="_blank"]').each((_i, elem) => {
      const $link = $(elem);
      $link.data("external-link", new ExternalLink($link));
    });
    updateExternalDomainLinks($container);
  }


  /**
   * Finalizes the new comment creation after the comment adding finishes
   * successfully.
   * @private
   * @param {jQuery} $parent - The parent comment element to finalize.
   * @param {Boolean} fromCurrentUser - A boolean indicating whether the user
   *   herself was the author of the new comment.
   * @returns {Void} - Returns nothing
   */
  _finalizeCommentCreation($parent, fromCurrentUser) {
    if (fromCurrentUser) {
      const $add = $("> .add-comment", $parent);
      const $text = $("textarea", $add);
      const characterCounter = $text.data("remaining-characters-counter");
      $text.val("");
      if (characterCounter) {
        characterCounter.updateStatus();
      }
      if (!$add.parent().is(".comments")) {
        $add.addClass("hide");
      }
    }

    // Restart the polling
    this._pollComments();
  }

  /**
   * Sets a timeout to poll new comments.
   * @private
   * @returns {Void} - Returns nothing
   */
  _pollComments(page = 1) {
    this._stopPolling();
    if (this.pollTimeout) {
      clearTimeout(this.pollTimeout);
    }
    this.pollTimeout = setTimeout(() => {
      if (!this.polledPages.has(page)) {
        this._fetchComments(() => {}, page);
        this.polledPages.add(page);
        this._stopPolling();
      }
    }, this.pollingInterval);
  }

  /**
   * Sends an ajax request based on current
   * params to get comments for the component
   * @private
   * @param {Function} successCallback A callback that is called after a
   *   successful fetch
   * @returns {Void} - Returns nothing
   */
  _fetchComments(successCallback = null, page = this.currentPage) {
    if (this.isFetchingComments) return;

    this.isFetchingComments = true;

    Rails.ajax({
      url: this.commentsUrl,
      type: "GET",
      data: new URLSearchParams({
        "commentable_gid": this.commentableGid,
        "root_depth": this.rootDepth,
        "order": this.order,
        "after": this.lastCommentId,
        "page": page,
        ...(this.toggleTranslations && { "toggle_translations": this.toggleTranslations }),
      }),
      success: (data) => {
        const $commentsContainer = $(".comment-threads", this.$element);
        if (page === this.currentPage) {
          $commentsContainer.html(data.html);
          this._initializeComments($commentsContainer);
        } else {
          $commentsContainer.append(data.html);
          this._initializeComments($commentsContainer);
        }

        if (successCallback) {
          successCallback();
        }

        this.isLoading = false;
        this.isFetchingComments = false;
        this._setLoading();
      },
      error: () => {
        this.isLoading = false;
        this.isFetchingComments = false;
        this._setLoading();
      }
    });
  }

  /**
   * Stops polling for new comments.
   * @private
   * @returns {Void} - Returns nothing
   */
  _stopPolling() {
    if (this.pollTimeout) {
      clearTimeout(this.pollTimeout);
    }
  }

  /**
   * Sets the loading comments element visible in the view.
   * @private
   * @returns {Void} - Returns nothing
   */
  _setLoading() {
    const $container = $("> .comments-container", this.$element);
    const $comments = $("> .comments", $container);
    const $loading = $(".loading-comments");
    const loadMoreButton = document.getElementById("load-more-comments");

    if (this.isLoading) {
      $comments.addClass("hide");
      if (loadMoreButton) loadMoreButton.style.display = "none";
      $loading.removeClass("hide");
    } else {
      $comments.removeClass("hide");
      if (loadMoreButton) loadMoreButton.style.display = "block";
      $loading.addClass("hide");
    }

    if(this.currentPage == this.maxPages){
      if (loadMoreButton) loadMoreButton.style.display = "none";
    }
  }

  /**
   * Event listener for the ordering links.
   * @private
   * @returns {Void} - Returns nothing
   */
  _onInitOrder() {
    this._stopPolling();
    this.reordered = true;
    this.currentPage = 1;
    this.polledPages.clear();
    this.isLoading = true;
    this._setLoading();
    this._fetchComments(() => {
      this.reordered = false;
    }, 1);
  }

  /**
   * Event listener for the opinion toggle buttons.
   * @private
   * @param {Event} ev - The event object.
   * @returns {Void} - Returns nothing
   */
  _onToggleOpinion(ev) {
    let $btn = $(ev.target);
    if (!$btn.is(".button")) {
      $btn = $btn.parents(".button");
    }

    const $add = $btn.closest(".add-comment");
    const $form = $("form", $add);
    const $opinionButtons = $(".opinion-toggle .button", $add);
    const $selectedState = $(".opinion-toggle .selected-state", $add);
    const $alignment = $(".alignment-input", $form);

    $opinionButtons.removeClass("is-active").attr("aria-pressed", "false");
    $btn.addClass("is-active").attr("aria-pressed", "true");

    if ($btn.is(".opinion-toggle--ok")) {
      $alignment.val(1);
    } else if ($btn.is(".opinion-toggle--meh")) {
      $alignment.val(0);
    } else if ($btn.is(".opinion-toggle--ko")) {
      $alignment.val(-1);
    }

    // Announce the selected state for the screen reader
    $selectedState.text($btn.data("selected-label"));
  }

  /**
   * Event listener for the comment field text input.
   * @private
   * @param {Event} ev - The event object.
   * @returns {Void} - Returns nothing
   */
  _onTextInput(ev) {
    const $text = $(ev.target);
    const $add = $text.closest(".add-comment");
    const $form = $("form", $add);
    const $submit = $("button[type='submit']", $form);

    if ($text.val().length > 0) {
      $submit.removeAttr("disabled");
    } else {
      $submit.attr("disabled", "disabled");
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const loadMoreButton = document.getElementById("load-more-comments");
  const commentsComponent = new CommentsComponent($(".comments-container"), {
    commentableGid: loadMoreButton.getAttribute("data-commentable-gid"),
    commentsUrl: "/comments",
    order: loadMoreButton.getAttribute("data-order"),
    commentsCount: parseInt(loadMoreButton.getAttribute("data-comments-count"), 10)
  });

  if (loadMoreButton) {
    const loadMoreHandler = (event) => {
      const page = parseInt(loadMoreButton.getAttribute("data-page"), 10);
      const newPage = page + 1;
      commentsComponent._loadMoreComments(newPage);
    };

    loadMoreButton.addEventListener("click", loadMoreHandler);
  }

  commentsComponent.mountComponent();
});
